/**
 * Answering an on-screen choice by voice.
 *
 * In conversation mode the copilot often ends a turn with a card of options.
 * Saying "the first one" or "sales leaders" should tick that option, not
 * arrive as a new message the model has to re-interpret — the card stays on
 * screen unanswered and the turn goes sideways.
 *
 * Matching is local and deliberately literal: an ordinal, the word
 * "recommended", the label itself, or enough of the label's distinctive
 * words. Anything less certain falls through and is sent as a message,
 * which is the behaviour we had before.
 */

// "second" is unambiguous; a bare "one" is not — "the second one" ends in
// the word for the first option. Strong forms win, weak ones only when they
// are the whole point of the sentence.
const STRONG = [
  /\b(first|1st)\b/,
  /\b(second|2nd)\b/,
  /\b(third|3rd)\b/,
  /\b(fourth|4th)\b/,
  /\b(fifth|5th)\b/
];
const NUMBERED = i => new RegExp(`\\b(option|number|choice)\\s*(${i + 1}|${["one", "two", "three", "four", "five"][i]})\\b`);
const BARE = i => new RegExp(`^(the\\s+)?(${i + 1}|${["one", "two", "three", "four", "five"][i]})(\\s+one)?$`);

// Words that carry no signal in a sales-tool option label.
const STOP = new Set([
  "the", "a", "an", "and", "or", "at", "in", "of", "for", "to", "with", "on",
  "option", "one", "please", "lets", "let", "go", "choose", "pick", "select",
  "that", "this", "it", "yes", "ok", "okay", "companies", "company"
]);

const norm = text =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s/&-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const words = text => norm(text).split(" ").filter(w => w && !STOP.has(w));

/**
 * Which option did they mean? Returns { index, reason } or null.
 * `options` is [{ label, description }] in the order shown on screen.
 */
export const matchChoice = (spoken, options = []) => {
  const said = norm(spoken);
  if (!said || !options.length) return null;

  // "the recommended one" — the label carries the marker.
  if (/\brecommend/.test(said)) {
    const i = options.findIndex(o => /recommend/i.test(o.label || ""));
    if (i >= 0) return { index: i, reason: "recommended" };
  }

  // "the last one"
  if (/\blast\b/.test(said)) return { index: options.length - 1, reason: "ordinal" };

  // "the second one", "number three", "option 2", or just "two".
  for (let i = 0; i < Math.min(STRONG.length, options.length); i += 1) {
    if (STRONG[i].test(said) || NUMBERED(i).test(said) || BARE(i).test(said)) {
      return { index: i, reason: "ordinal" };
    }
  }

  // The label itself, or most of its distinctive words.
  let best = null;
  options.forEach((option, index) => {
    const label = norm(option.label);
    if (label && said.includes(label)) {
      best = { index, score: 1, reason: "label" };
      return;
    }
    const target = words(`${option.label} ${option.description || ""}`);
    if (!target.length) return;
    const saidWords = new Set(words(said));
    const hits = target.filter(w => saidWords.has(w)).length;
    const score = hits / Math.min(target.length, 6);
    if (score > 0 && (!best || score > best.score)) best = { index, score, reason: "words" };
  });

  // A winner needs to be clearly ahead, or it is a guess dressed up as one.
  if (best && best.score >= 0.5) return { index: best.index, reason: best.reason };
  return null;
};
